import { Popper, PopperProps } from '@mui/material'
import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import styles, {
  StyledListbox,
} from 'ext-ui/components/Dropdown/components/DropdownMenu/DropdownMenu.style'
import { TextInputVariant } from 'ext-ui/components/TextInput/TextInput'
import TextInputInternal from 'ext-ui/components/TextInput/TextInputInternal/TextInputInternal'
import React, { ComponentPropsWithRef, PropsWithChildren } from 'react'
import SvgSearch from 'src/components/Svgs/SvgSearch'
import { useTranslate } from 'src/localization/Trns'
import { InfiniteScrollProps } from 'src/types'

export type DropdownMenuProps = ComponentPropsWithRef<'ul'> &
  PropsWithChildren & {
    variant?: TextInputVariant
    width?: number
    /**
     * how many options are visible in the DropdownMenu before scrolling is necessary
     * @default 5
     */
    // TODO: this should just be a height, because anything can be passed as children
    dropdownOptionsCount?: number
    open: boolean
    infiniteScrollProps?: InfiniteScrollProps<string>
    getInputProps?: (options?: any) => any
    className?: string
    searchProps?: ComponentPropsWithRef<'input'>
    searchable?: boolean
    onReset?: () => void
    anchorEl: PopperProps['anchorEl']
    container?: PopperProps['container']
    menuTestId?: string
  }

const isEndOfScroll = (el: HTMLElement) =>
  el.scrollTop + el.clientHeight >= el.scrollHeight - 1

const DropdownMenu = React.forwardRef<HTMLUListElement, DropdownMenuProps>(
  (props, ref) => {
    const translate = useTranslate()
    const {
      variant = 'medium',
      width,
      dropdownOptionsCount = 5,
      open,
      infiniteScrollProps,
      searchProps,
      searchable,
      onReset,
      anchorEl,
      container,
      className,
      children,
      menuTestId,
      ...listboxProps
    } = props

    const {
      placeholder = translate('Type or select from list'),
      ...searchPropsRest
    } = searchProps || {}

    const onScroll: React.UIEventHandler<HTMLUListElement> = (event) => {
      const el = event.currentTarget
      if (
        infiniteScrollProps &&
        !infiniteScrollProps.loading &&
        !infiniteScrollProps.complete &&
        isEndOfScroll(el)
      ) {
        infiniteScrollProps.fetch()
      }
    }

    const baseTestId = menuTestId ?? 'dropdown'

    return (
      <Popper
        open={open}
        anchorEl={anchorEl}
        container={container}
        placement="bottom-start"
        css={styles}
        popperOptions={{
          modifiers: [
            { name: 'hide', enabled: true },
            { name: 'preventOverflow', enabled: true },
            { name: 'flip', enabled: false },
          ],
        }}
        className={cn(className, variant)}
        data-testid={`${baseTestId}-popper`}
      >
        <StyledListbox
          width={width}
          dropdownOptionsCount={dropdownOptionsCount}
          className={'menu'}
          data-testid={`${baseTestId}-listbox`}
        >
          {searchProps && (
            <div
              className={cn('search-container', variant, {
                hidden: !searchable,
              })}
            >
              <SvgSearch
                className={'search-icon'}
                viewBox={'0 0 24 24'}
                width={variant === 'small' ? 16 : 24}
                height={variant === 'small' ? 16 : 24}
              />
              <TextInputInternal
                className={'search-input'}
                {...searchPropsRest}
                variant={'small'}
                placeholder={placeholder}
                data-testid={'popper-input'}
              />
            </div>
          )}
          <ul
            {...listboxProps}
            className={'scroll-container'}
            ref={ref}
            onScroll={onScroll}
          >
            {children}
          </ul>
          {onReset && (
            <div className={'reset-container'}>
              <Button
                className={'reset-button'}
                variant={'no-border'}
                onMouseDown={onReset}
                onClick={onReset}
              >
                {translate('Reset')}
              </Button>
            </div>
          )}
        </StyledListbox>
      </Popper>
    )
  },
)

export default DropdownMenu
